
import { useState, useEffect, RefObject } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';

/**
 * Hook to manage video element events
 */
export function useVideoEvents(
  videoRef: RefObject<HTMLVideoElement>,
  features: VideoFeatures
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  const [isVertical, setIsVertical] = useState(false);

  // Detecta orientação do vídeo a partir das dimensões intrínsecas
  // (disponíveis após `loadedmetadata`).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateOrientation = () => {
      if (video.videoWidth && video.videoHeight) {
        setIsVertical(video.videoHeight > video.videoWidth);
      }
    };

    updateOrientation();
    video.addEventListener('loadedmetadata', updateOrientation);
    return () => video.removeEventListener('loadedmetadata', updateOrientation);
  }, [videoRef]);

  // Set up the video when specific features change
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Handle speed changes - ensure playbackSpeed is a valid number
    const playbackSpeed = Number(features.playbackSpeed);
    if (!isNaN(playbackSpeed) && isFinite(playbackSpeed) && playbackSpeed > 0) {
      video.playbackRate = playbackSpeed;
    } else {
      // Set a safe default if the value is invalid
      video.playbackRate = 1.0;
      console.warn('Invalid playbackSpeed value, using default 1.0 instead');
    }
  }, [features.playbackSpeed, videoRef]);

  // Handle loop setting separately
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.loop = features.loopVideo || false;
  }, [features.loopVideo, videoRef]);

  // Handle muted state separately
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = isMuted;
    video.volume = isMuted ? 0 : volume;
  }, [volume, isMuted, videoRef]);

  // Restore last watched position when video metadata is ready.
  // Source loading itself is handled declaratively by VideoElement (<source> for MP4
  // or hls.js attach for m3u8). Setting video.src + video.load() here was aborting
  // in-flight range requests on every re-render and caused 900+ duplicated fetches.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !features.continueWatching?.enabled || !features.videoId) return;

    const restorePosition = () => {
      const lastPosition = localStorage.getItem(`video-position-${features.videoId}`);
      if (lastPosition) {
        const parsed = parseFloat(lastPosition);
        if (!isNaN(parsed)) {
          video.currentTime = parsed;
        }
      }
    };

    video.addEventListener('loadedmetadata', restorePosition);
    return () => video.removeEventListener('loadedmetadata', restorePosition);
  }, [features.continueWatching?.enabled, features.videoId, videoRef]);

  // Event handlers
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    
    setCurrentTime(video.currentTime);
    if (features.videoId) {
      localStorage.setItem(`video-position-${features.videoId}`, video.currentTime.toString());
    }
  };
  
  const handleDurationChange = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration);
  };
  
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  
  const handleVolumeChange = () => {
    const video = videoRef.current;
    if (!video) return;
    
    setVolume(video.volume);
    setIsMuted(video.muted);
  };

  // Handle video ended event
  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    if (features.loopVideo || features.haveRestart) {
      video.currentTime = 0;
      video.play().catch(err => console.error("Error auto-replaying video:", err));
    }
  };

  return {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isVertical,
    setIsMuted,
    setVolume,
    handleTimeUpdate,
    handleDurationChange,
    handlePlay,
    handlePause,
    handleVolumeChange,
    handleVideoEnded
  };
}
